import { useCallback, useMemo } from "react"

import { useArchiveAndRecreateDialog } from "./ArchiveAndRecreateConfirmDialog/ArchiveAndRecreateConfirmDialog"
import { CreateSettlementButton } from "./CreateSettlementButton/CreateSettlementButton"
import { useRereservationStatusFixModal } from "./RereservationStatusFixModal/RereservationStatusFixModal"
import { useSendEmailDialog } from "./SendEmailConfirmDialog/SendEmailConfirmDialog"
import { useUpdateSettlementStatusDialog } from "./UpdateSettlementStatusConfirmDialog/UpdateSettlementStatusConfirmDialog"
import styles from "./SettlementSubTab.module.scss"
import SettlementTable from "./SettlementTable/SettlementTable"
import useExcelDownload from "./useExcelDownload"

import {
	getFragmentData,
	graphql,
	type FragmentType,
} from "@/company_admin/gql/__generated__"
import {
	getLastYearMonth,
	isSameYearMonth,
	type YearMonthValue,
} from "@/company_admin/utils/yearMonth"

// --- Fragment ---

export const settlementFragment = graphql(/* GraphQL */ `
	fragment SettlementFields on Settlement {
		id
		yearMonth {
			year
			month
		}
		status
		finalAmount {
			isPayout
		}
		...SettlementTableFields
	}
`)

type Props = {
	settlements: readonly FragmentType<typeof settlementFragment>[]
	loading: boolean
	error: unknown
	refetchSettlements: () => void
	isSouhlAdmin: boolean
	accountingGroupId: string
}

const SettlementSubTab = ({
	settlements: settlementsRaw,
	loading,
	error,
	refetchSettlements,
	isSouhlAdmin,
	accountingGroupId,
}: Props) => {
	const settlements = useMemo(
		() => getFragmentData(settlementFragment, settlementsRaw) ?? [],
		[settlementsRaw]
	)

	const lastMonth: YearMonthValue = useMemo(
		() => getLastYearMonth(new Date()),
		[]
	)

	const lastMonthSettlementExists = useMemo(
		() => settlements.some((s) => isSameYearMonth(s.yearMonth, lastMonth)),
		[settlements, lastMonth]
	)

	// Settlement lookup helper for dialog/modal openers
	const findSettlement = useCallback(
		(id: string) => settlements.find((s) => s.id === id),
		[settlements]
	)

	// Archive & Recreate dialog
	const archiveDialog = useArchiveAndRecreateDialog({
		onRecreateComplete: () => {
			refetchSettlements()
		},
	})

	const handleRecreate = useCallback(
		(settlementId: string) => {
			const s = findSettlement(settlementId)
			if (s) {
				archiveDialog.openDialog(settlementId, s.yearMonth)
			}
		},
		[findSettlement, archiveDialog.openDialog]
	)

	// Excel download
	const { downloadExcel, downloadingSettlementId } = useExcelDownload()

	// Rereservation Status Fix modal
	const statusFixModal = useRereservationStatusFixModal({
		onUpdateComplete: () => {
			refetchSettlements()
		},
	})

	const handleOpenRereservationStatusFix = useCallback(
		(settlementId: string) => {
			const s = findSettlement(settlementId)
			if (s) {
				statusFixModal.openModal(settlementId, s.yearMonth)
			}
		},
		[findSettlement, statusFixModal.openModal]
	)

	// Update Settlement Status dialog (generic for all transitions)
	const updateStatusDialog = useUpdateSettlementStatusDialog({
		onUpdateComplete: () => {
			refetchSettlements()
		},
	})

	const handleUpdateStatus = useCallback(
		(settlementId: string, targetStatus: string) => {
			const s = findSettlement(settlementId)
			if (s) {
				updateStatusDialog.openDialog(
					settlementId,
					s.finalAmount.isPayout,
					targetStatus,
					s.yearMonth
				)
			}
		},
		[findSettlement, updateStatusDialog.openDialog]
	)

	// Send Email dialog
	const sendEmailDialog = useSendEmailDialog({
		onSendComplete: () => {
			refetchSettlements()
		},
	})

	const handleSendEmail = useCallback(
		(settlementId: string) => {
			const s = findSettlement(settlementId)
			if (s) {
				sendEmailDialog.openDialog(settlementId, s.yearMonth)
			}
		},
		[findSettlement, sendEmailDialog.openDialog]
	)

	if (loading) {
		return <div className={styles.loading}>読み込み中...</div>
	}

	if (error) {
		return (
			<div className={styles.error}>
				データの取得に失敗しました。ページを再読み込みしてください。
			</div>
		)
	}

	return (
		<div className={styles.settlement_subtab}>
			{/* 注釈テキスト */}
			<div className={styles.annotations}>
				<p>※ 各相殺明細は、作成当時の設定で計算した値を表示しています</p>
				<p>※ 毎月1日中に、前月成果分の相殺明細が作成されます</p>
			</div>

			{isSouhlAdmin && !lastMonthSettlementExists && (
				<CreateSettlementButton
					accountingGroupId={accountingGroupId}
					lastMonth={lastMonth}
					onCreateComplete={refetchSettlements}
				/>
			)}

			{/* 明細一覧テーブル */}
			<SettlementTable
				settlements={settlements}
				isSouhlAdmin={isSouhlAdmin}
				onRecreate={handleRecreate}
				onOpenRereservationStatusFix={handleOpenRereservationStatusFix}
				onUpdateStatus={handleUpdateStatus}
				onSendEmail={handleSendEmail}
				onExcelDownload={downloadExcel}
				downloadingSettlementId={downloadingSettlementId}
			/>

			{/* ダイアログ・モーダル */}
			{archiveDialog.render()}
			{statusFixModal.render()}
			{updateStatusDialog.render()}
			{sendEmailDialog.render()}
		</div>
	)
}

export default SettlementSubTab
